import {
	Settings,
	Orientation,
	DeviceType,
	DesignSourceFromUrl,
	SofaRange,
	ApplicationParams,
} from 'sofa-reborn-react';

import { ThunkResult, AppDispatch, RootState } from '../..';
import {
	setPersistedConfiguration,
	setPredefinedConfigurationId,
	setGalleryPredefinedConfigurationId,
} from '../../sofaConfiguration';
import { setEnvironmentalLighting, setLightIntensity } from '../../scene';
import { localeService, UrlService, IFrameHandler, resizeHandler } from '../../../services';

import { setCoverOnly } from '../../cover';
import { fetchLocalizationThunk } from '../../localization/thunks';
import ThunksUtil from '../utils/ThunksUtil';

import {
	setAssetFolder,
	showDebug,
	setUseIndividualAssets,
	setShowIpexGalleryButton,
	setIsInUpptackaPlatform,
	setDisableBulkFetch,
	setUsePersistedConfig,
	statisticsQrPlanningEnabled,
} from '../actions';
import {
	setCurrentLightbox,
	setSofaRange,
	setAppView,
	appInitiated,
	setIdleTime,
} from '../actions';
import { resetScreensaverTimeout, showScreensaver } from '../../screensaver/actions';
import { setFeatures } from '../../settings/actions';
import {
	fetchVpcSuccess,
	setNoSummaryPageBackButton,
	setDesignSource,
	setShowSummary,
} from '../../checkout/actions';
import { setPlatform, setOrientation, setDeviceType } from '../../deviceInfo/actions';
import { setLocale, setStoreId, setZipCode } from '../../localization/actions';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import { CookieInformationService } from '@inter-ikea-kompis/services';

export default class AppInitThunk {
	public init(): ThunkResult {
		return async (dispatch, getState): Promise<void> => {
			const state = getState();
			const { settings, sofaRange } = state.settings;

			/* Close additional information if open */
			if (state.application.currentLightbox === 'additional-information') {
				dispatch(setCurrentLightbox('none'));
			}

			const platform = UrlService.getPlatform();
			dispatch(setPlatform(platform));

			const params = UrlService.getApplicationParams();
			this.handleKiosk(platform, params, dispatch);
			this.handleSofaRange(sofaRange, dispatch);
			this.handleIndividualAssets(params, dispatch);
			this.handleLocale(dispatch);
			this.handleStoreId(dispatch, platform, settings);
			this.handleZipCode(params, dispatch, platform, settings);
			this.handleIframe(settings, dispatch);
			this.handleFeatures(params, dispatch);

			await dispatch(fetchLocalizationThunk());

			this.handleShowDebug(params, dispatch);
			this.handleAssetFolder(params, dispatch);
			this.handleLight(params, dispatch);
			this.handleSummaryPage(params, getState, dispatch);
			this.handleVpcSource(params, dispatch);
			this.handleCover(params, dispatch);

			dispatch(appInitiated());
		};
	}

	private handleLocale(dispatch) {
		const locale = localeService.getLocale();
		dispatch(setLocale(locale));
	}

	private handleKiosk(platform: string, params: ApplicationParams, dispatch) {
		if (platform === 'kiosk') {
			if (params.activeScreenSaver && !params.isInUpptackaPlatform) {
				dispatch(showScreensaver(true));
				ThunksUtil.removeParamFromUrl('activeScreenSaver');
			}

			const showIpexGallery = params.showIpexGallery;
			if (showIpexGallery) {
				dispatch(setShowIpexGalleryButton(true));
				const idleTime = params.idleTime ? parseFloat(params.idleTime) : null;
				if (idleTime) {
					dispatch(setIdleTime(idleTime));
				}
				dispatch(resetScreensaverTimeout());
				ThunksUtil.removeParamFromUrl('ipexGallery');
			}

			if (params.isInUpptackaPlatform && UrlService.getPlatform() === 'kiosk') {
				dispatch(setIsInUpptackaPlatform(true));
			}
		}
	}

	private handleSofaRange(sofaRange: string, dispatch) {
		const newSofaRange = UrlService.getSofaRange() || sofaRange;
		if (newSofaRange !== sofaRange) {
			dispatch(setCurrentLightbox('product-intro-gallery'));
			dispatch(setAppView('build-view'));
			dispatch(fetchVpcSuccess(null));
			dispatch(setPersistedConfiguration(null));
			dispatch(setPredefinedConfigurationId(null));
		}
		dispatch(setSofaRange(newSofaRange as SofaRange));
	}

	private handleIndividualAssets(params: ApplicationParams, dispatch) {
		const useIndividualAssets = params.useIndividualAssets;
		if (useIndividualAssets) {
			dispatch(setUseIndividualAssets(true));
		}
	}

	private handleIframe(settings: Settings, dispatch) {
		const iframeHandler = new IFrameHandler(settings);
		resizeHandler.addListener((orientation: Orientation, deviceType: DeviceType) => {
			dispatch(setOrientation(orientation));
			dispatch(setDeviceType(deviceType));

			iframeHandler.updateIframeHeight();
		});
	}

	private handleStoreId(dispatch, platform: string, settings: Settings) {
		const storeId = UrlService.getStoreId();
		if (storeId) {
			dispatch(setStoreId(storeId));
		} else if (platform !== 'kiosk') {
			this.setStoreIdFromCookies(dispatch, settings);
		}
	}

	private handleZipCode(params: ApplicationParams, dispatch, platform: string, settings: Settings) {
		if (params.zipCode) {
			dispatch(setZipCode(params.zipCode, false));
			ThunksUtil.removeParamFromUrl('zipCode');
		} else if (platform !== 'kiosk') {
			this.setZipCodeFromCookies(dispatch, settings);
		}
	}

	private handleFeatures(params: ApplicationParams, dispatch) {
		if (params.disableBulkFetch) {
			dispatch(setDisableBulkFetch(true));
		}
		if (params.usePersistedConfig) {
			dispatch(setUsePersistedConfig(true));
		}
		if (params.features) {
			const formattedFeatures = params.features.split(',');
			dispatch(setFeatures(formattedFeatures));
		}
	}

	private handleShowDebug(params: ApplicationParams, dispatch) {
		const hasDebug = params.showDebug;
		if (hasDebug) {
			dispatch(showDebug(true));
		}
	}

	private handleAssetFolder(params: ApplicationParams, dispatch) {
		const assetFolder = params.assetFolder;
		if (assetFolder) {
			dispatch(setAssetFolder(assetFolder));
		} else {
			dispatch(setAssetFolder(null));
		}
	}

	private handleLight(params: ApplicationParams, dispatch) {
		const evironmentalLighting = params.environmentalLightning;
		if (evironmentalLighting) {
			dispatch(setEnvironmentalLighting(evironmentalLighting));
		}

		const lightIntensity = params.lightIntensity;
		if (lightIntensity) {
			dispatch(setLightIntensity(parseFloat(lightIntensity)));
		}
	}

	private handleSummaryPage(params: ApplicationParams, getState, dispatch) {
		const showSummaryPage = params.showSummaryPage;
		if (showSummaryPage) {
			dispatch(setAppView('summary-page'));
			dispatch(setNoSummaryPageBackButton(true));
			ThunksUtil.removeParamFromUrl('showSummaryPage');
		} else {
			const state: RootState = getState();
			const deviceType = state.deviceInfo.deviceType;
			const parentParams = deviceType === 'mobile' ? UrlService.getParentParams() : null;
			if (parentParams && parentParams.vpcSource === DesignSourceEnum.qr) {
				dispatch(setShowSummary(true));
				dispatch(setAppView('summary-page'));
				dispatch(statisticsQrPlanningEnabled());
			}
		}
	}

	private handleVpcSource(params: ApplicationParams, dispatch) {
		const designSource: DesignSourceEnum = params.vpcSource || DesignSourceEnum.startFromScratch;
		const vpcCode: string | null = UrlService.getVpcOrSprId();

		if (designSource && vpcCode) {
			dispatch(setDesignSource({ source: designSource, code: vpcCode } as DesignSourceFromUrl));
		}
	}

	private handleCover(params: ApplicationParams, dispatch) {
		const cover = params.cover;
		if (cover) {
			dispatch(setCoverOnly(true));
			dispatch(setCurrentLightbox('none'));
			dispatch(setGalleryPredefinedConfigurationId('blank'));
			dispatch(setPersistedConfiguration(null));
		}
	}

	private async setStoreIdFromCookies(dispatch: AppDispatch, settings: Settings): Promise<void> {
		const service = new CookieInformationService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const storeId: string | null = await service.getStoreId();

		if (storeId) {
			dispatch(setStoreId(storeId));
		}
	}

	private async setZipCodeFromCookies(dispatch: AppDispatch, settings: Settings): Promise<void> {
		const service = new CookieInformationService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const zipCode: string | null = await service.getZipCode();

		if (zipCode) {
			dispatch(setZipCode(zipCode, false));
		}
	}
}
